/**
 * PodcastsSection - Main podcast management section orchestrator
 *
 * Single Responsibility: Orchestrates podcast management views and state.
 * Coordinates between list view, detail view, filters, and dialogs.
 *
 * Note: Still contains tab implementations and forms that will be extracted in future phases.
 * Target: Reduce to <400 lines after extracting tab components and dialogs.
 */
package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.components.IconButton
import com.castly.admin.ui.components.ConfirmDialog
import com.castly.admin.ui.icons.IconEdit
import com.castly.admin.ui.icons.IconTrash
import com.castly.admin.ui.icons.IconCheck
import com.castly.admin.ui.icons.IconMenu
import com.castly.admin.ui.icons.IconPlus
import com.castly.admin.ui.icons.IconRss
import com.castly.admin.ui.icons.IconRefreshCw
import com.castly.admin.ui.podcasts.PodcastListView
import com.castly.admin.ui.podcasts.PodcastDetailView
import com.castly.admin.ui.podcasts.PodcastFilters
import com.castly.admin.ui.podcasts.components.Pagination
import com.castly.admin.ui.podcasts.DetailTab
import com.castly.admin.ui.podcasts.PodcastTypeChip
import com.castly.admin.ui.podcasts.dialogs.PodcastFormDialog
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

sealed class PodcastViewState {
    object List : PodcastViewState()
    object Create : PodcastViewState()
    object ImportRss : PodcastViewState()
    object BulkImportRss : PodcastViewState()
    data class Edit(val podcast: PodcastDto) : PodcastViewState()
}

@Composable
fun PodcastsSection(apiClient: ApiClient, filterType: PodcastType? = null) {
    val colors = AppTheme.colors
    var viewState by remember { mutableStateOf<PodcastViewState>(PodcastViewState.List) }
    var podcasts by remember { mutableStateOf<List<PodcastDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var totalCount by remember { mutableStateOf(0) }
    var currentPage by remember { mutableStateOf(1) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedPodcast by remember { mutableStateOf<PodcastDto?>(null) }
    var activeTab by remember { mutableStateOf(DetailTab.DATA) }
    var tags by remember { mutableStateOf<List<TagWithCountDto>>(emptyList()) }
    var selectedTags by remember { mutableStateOf<MutableSet<String>>(mutableSetOf()) }
    var showDeleted by remember { mutableStateOf(false) }
    // Local type filter that can be changed by filter chips; initialized from sidebar selection
    var activeFilterType by remember(filterType) { mutableStateOf(filterType) }

    val scope = rememberCoroutineScope()

    fun loadPodcasts() {
        scope.launch {
            loading = true
            error = null
            try {
                val response = apiClient.listPodcasts(
                    page = currentPage,
                    query = searchQuery.ifBlank { null },
                    type = activeFilterType?.name,
                    tags = selectedTags.toList(),
                    includeDeleted = showDeleted
                )
                podcasts = response.items
                totalCount = response.total
            } catch (e: Exception) {
                error = e.message ?: "Failed to load podcasts"
            } finally {
                loading = false
            }
        }
    }

    // Load tags filtered by type
    fun loadTags() {
        scope.launch {
            try {
                tags = apiClient.listTags(type = activeFilterType?.name)
            } catch (e: Exception) {
                // Silently fail - tags are optional for filtering
            }
        }
    }

    // Load tags when filter type changes
    LaunchedEffect(activeFilterType) {
        loadTags()
    }

    // Reload podcasts when filter type, page, search, tags, or showDeleted change
    LaunchedEffect(activeFilterType, currentPage, searchQuery, selectedTags, showDeleted) {
        loadPodcasts()
    }

    when (val state = viewState) {
        is PodcastViewState.List -> {
            // Podcast type filter chips
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    marginBottom(Spacing.md)
                    property("flex-wrap", "wrap")
                    alignItems(AlignItems.Center)
                }
            }) {
                // "All" chip
                Button({
                    onClick { activeFilterType = null; currentPage = 1 }
                    style {
                        padding(6.px, 14.px)
                        backgroundColor(if (activeFilterType == null) Color(colors.primary) else Color(colors.surfaceHover))
                        color(if (activeFilterType == null) Color("#ffffff") else Color(colors.text))
                        border(1.px, LineStyle.Solid, if (activeFilterType == null) Color(colors.primary) else Color(colors.border))
                        borderRadius(16.px)
                        cursor("pointer")
                        fontSize(13.px)
                        fontWeight(if (activeFilterType == null) "600" else "500")
                        property("transition", "all 0.2s")
                    }
                }) { Text("All") }

                PodcastType.values().forEach { type ->
                    val isActive = activeFilterType == type
                    Button({
                        onClick { activeFilterType = type; currentPage = 1 }
                        style {
                            padding(6.px, 14.px)
                            backgroundColor(if (isActive) Color(colors.primary) else Color(colors.surfaceHover))
                            color(if (isActive) Color("#ffffff") else Color(colors.text))
                            border(1.px, LineStyle.Solid, if (isActive) Color(colors.primary) else Color(colors.border))
                            borderRadius(16.px)
                            cursor("pointer")
                            fontSize(13.px)
                            fontWeight(if (isActive) "600" else "500")
                            property("transition", "all 0.2s")
                        }
                    }) { Text(type.displayName) }
                }
            }

            PodcastsSplitView(
                podcasts = podcasts,
                loading = loading,
                error = error,
                totalCount = totalCount,
                currentPage = currentPage,
                searchQuery = searchQuery,
                selectedPodcast = selectedPodcast,
                activeTab = activeTab,
                apiClient = apiClient,
                tags = tags,
                selectedTags = selectedTags,
                showDeleted = showDeleted,
                onSearchChange = { searchQuery = it; currentPage = 1 },
                onPageChange = { currentPage = it },
                onFilterTagsChange = { newTags ->
                    selectedTags = newTags
                    currentPage = 1
                },
                onToggleShowDeleted = {
                    showDeleted = !showDeleted
                    currentPage = 1
                },
                onCreate = { viewState = PodcastViewState.Create },
                onImportRss = { viewState = PodcastViewState.ImportRss },
                onBulkImportRss = { viewState = PodcastViewState.BulkImportRss },
                onPodcastClick = {
                    selectedPodcast = it
                    activeTab = DetailTab.DATA
                },
                onTabChange = { activeTab = it },
                onEdit = { viewState = PodcastViewState.Edit(it) },
                onDelete = { podcast ->
                    scope.launch {
                        try {
                            apiClient.deletePodcast(podcast.id)
                            if (selectedPodcast?.id == podcast.id) {
                                selectedPodcast = null
                            }
                            loadPodcasts()
                        } catch (e: Exception) {
                            error = "Failed to delete: ${e.message}"
                        }
                    }
                },
                onRestore = { podcast ->
                    scope.launch {
                        try {
                            apiClient.restorePodcast(podcast.id)
                            loadPodcasts()
                        } catch (e: Exception) {
                            error = "Failed to restore: ${e.message}"
                        }
                    }
                },
                onPublish = { podcast ->
                    // Publish/unpublish functionality removed
                },
                onPodcastUpdate = { podcastId ->
                    scope.launch {
                        try {
                            val updated = apiClient.getPodcast(podcastId)
                            selectedPodcast = updated
                            loadPodcasts()
                        } catch (e: Exception) {
                            error = "Failed to refresh podcast: ${e.message}"
                        }
                    }
                },
                onClose = { selectedPodcast = null },
                onRefresh = { loadPodcasts() }
            )
        }
        is PodcastViewState.ImportRss -> {
            ImportRssDialog(
                apiClient = apiClient,
                onImport = { podcasts ->
                    viewState = PodcastViewState.List
                    loadPodcasts()
                    // Select the first imported podcast (if multiple feeds were imported)
                    selectedPodcast = podcasts.firstOrNull()
                },
                onCancel = { viewState = PodcastViewState.List },
                defaultType = filterType
            )
        }
        is PodcastViewState.BulkImportRss -> {
            BulkImportRssDialog(
                apiClient = apiClient,
                onImport = {
                    viewState = PodcastViewState.List
                    loadPodcasts()
                },
                onCancel = { viewState = PodcastViewState.List },
                defaultType = filterType
            )
        }
        is PodcastViewState.Create -> {
            PodcastFormDialog(
                podcast = null,
                onSave = { request ->
                    scope.launch {
                        try {
                            apiClient.createPodcast(request)
                            viewState = PodcastViewState.List
                            loadPodcasts()
                        } catch (e: Exception) {
                            error = "Failed to create: ${e.message}"
                        }
                    }
                },
                onCancel = { viewState = PodcastViewState.List },
                defaultType = filterType
            )
        }
        is PodcastViewState.Edit -> {
            PodcastFormDialog(
                podcast = state.podcast,
                onSave = { request ->
                    scope.launch {
                        try {
                            apiClient.updatePodcast(state.podcast.id, UpdatePodcastRequest(
                                title = request.title,
                                description = request.description,
                                author = request.author,
                                language = request.language,
                                explicit = request.explicit,
                                translations = request.translations,
                                type = request.type
                            ))
                            viewState = PodcastViewState.List
                            loadPodcasts()
                        } catch (e: Exception) {
                            error = "Failed to update: ${e.message}"
                        }
                    }
                },
                onCancel = { viewState = PodcastViewState.List }
            )
        }
    }
}

@Composable
private fun PodcastsSplitView(
    podcasts: List<PodcastDto>,
    loading: Boolean,
    error: String?,
    totalCount: Int,
    currentPage: Int,
    searchQuery: String,
    selectedPodcast: PodcastDto?,
    activeTab: DetailTab,
    apiClient: ApiClient,
    tags: List<TagWithCountDto>,
    selectedTags: MutableSet<String>,
    showDeleted: Boolean,
    onSearchChange: (String) -> Unit,
    onPageChange: (Int) -> Unit,
    onFilterTagsChange: (MutableSet<String>) -> Unit,
    onToggleShowDeleted: () -> Unit,
    onCreate: () -> Unit,
    onImportRss: () -> Unit,
    onBulkImportRss: () -> Unit,
    onPodcastClick: (PodcastDto) -> Unit,
    onTabChange: (DetailTab) -> Unit,
    onEdit: (PodcastDto) -> Unit,
    onDelete: (PodcastDto) -> Unit,
    onRestore: (PodcastDto) -> Unit,
    onPublish: (PodcastDto) -> Unit,
    onPodcastUpdate: (String) -> Unit,
    onClose: () -> Unit,
    onRefresh: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex)
            height(100.percent)
            gap(Spacing.lg)
        }
    }) {
        // Left Panel - Podcast List
        Div({
            style {
                flex(if (selectedPodcast != null) "0 0 40%" else "1")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("overflow", "auto")
            }
        }) {
            PodcastListPanel(
                podcasts = podcasts,
                loading = loading,
                error = error,
                totalCount = totalCount,
                currentPage = currentPage,
                searchQuery = searchQuery,
                selectedPodcastId = selectedPodcast?.id,
                tags = tags,
                selectedTags = selectedTags,
                showDeleted = showDeleted,
                onSearchChange = onSearchChange,
                onPageChange = onPageChange,
                onFilterTagsChange = onFilterTagsChange,
                onToggleShowDeleted = onToggleShowDeleted,
                onCreate = onCreate,
                onImportRss = onImportRss,
                onBulkImportRss = onBulkImportRss,
                onPodcastClick = onPodcastClick,
                onRestore = onRestore,
                onRefresh = onRefresh
            )
        }

        // Right Panel - Podcast Details
        if (selectedPodcast != null) {
            Div({
                style {
                    flex("1")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    backgroundColor(Color(colors.surface))
                    borderRadius(BorderRadius.lg)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    property("overflow", "hidden")
                    themeTransition()
                }
            }) {
                PodcastDetailView(
                    podcast = selectedPodcast,
                    activeTab = activeTab,
                    apiClient = apiClient,
                    onTabChange = onTabChange,
                    onEdit = onEdit,
                    onDelete = onDelete,
                    onPublish = onPublish,
                    onPodcastUpdate = onPodcastUpdate,
                    onClose = onClose,
                    dataTab = { podcast, api, onUpdate -> DataTab(podcast, api, onUpdate) },
                    episodesTab = { podcast, api, onUpdate -> EpisodesTab(podcast, api, onUpdate) },
                    tagsTab = { podcast, api -> TagsTab(podcast, api) },
                    translationsTab = { podcast, api, onUpdate -> TranslationsTab(podcast, api, onUpdate) }
                )
            }
        }
    }
}

@Composable
private fun PodcastListPanel(
    podcasts: List<PodcastDto>,
    loading: Boolean,
    error: String?,
    totalCount: Int,
    currentPage: Int,
    searchQuery: String,
    selectedPodcastId: String?,
    tags: List<TagWithCountDto>,
    selectedTags: MutableSet<String>,
    showDeleted: Boolean,
    onSearchChange: (String) -> Unit,
    onPageChange: (Int) -> Unit,
    onFilterTagsChange: (MutableSet<String>) -> Unit,
    onToggleShowDeleted: () -> Unit,
    onCreate: () -> Unit,
    onImportRss: () -> Unit,
    onBulkImportRss: () -> Unit,
    onPodcastClick: (PodcastDto) -> Unit,
    onRestore: (PodcastDto) -> Unit,
    onRefresh: () -> Unit
) {
    val colors = AppTheme.colors
    // Filters (search, tags)
    PodcastFilters(
        searchQuery = searchQuery,
        tags = tags,
        selectedTags = selectedTags,
        onSearchChange = onSearchChange,
        onFilterTagsChange = onFilterTagsChange,
        onCreate = onCreate,
        onImportRss = onImportRss,
        onBulkImportRss = onBulkImportRss
    )

    // Show Deleted toggle
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.sm)
            padding(Spacing.xs, Spacing.md)
        }
    }) {
        Button({
            onClick { onToggleShowDeleted() }
            style {
                padding(Spacing.xs, Spacing.md)
                fontSize(Typography.sm)
                backgroundColor(Color(if (showDeleted) colors.danger else colors.surfaceHover))
                color(Color(if (showDeleted) "#ffffff" else colors.textMuted))
                border(1.px, LineStyle.Solid, Color(if (showDeleted) colors.danger else colors.border))
                borderRadius(BorderRadius.sm)
                cursor("pointer")
                themeTransition()
            }
        }) { Text(if (showDeleted) "Hide Deleted" else "Show Deleted") }
    }

    // Split podcasts into active and deleted
    val activePodcasts = if (showDeleted) podcasts.filter { it.deletedAt == null } else podcasts
    val deletedPodcasts = if (showDeleted) podcasts.filter { it.deletedAt != null } else emptyList()

    // Active podcast list view
    PodcastListView(
        podcasts = activePodcasts,
        loading = loading,
        error = error,
        selectedPodcastId = selectedPodcastId,
        searchQuery = searchQuery,
        onPodcastClick = onPodcastClick
    )

    // Deleted podcasts section (only shown when showDeleted is enabled)
    if (showDeleted && deletedPodcasts.isNotEmpty()) {
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                border(1.px, LineStyle.Solid, Color(colors.danger))
                property("overflow", "hidden")
                marginTop(Spacing.md)
                themeTransition()
            }
        }) {
            Div({
                style {
                    padding(Spacing.sm, Spacing.md)
                    fontSize(Typography.sm)
                    fontWeight("600")
                    color(Color(colors.danger))
                    property("border-bottom", "1px solid ${colors.border}")
                }
            }) { Text("Deleted Podcasts (${deletedPodcasts.size})") }
            deletedPodcasts.forEach { podcast ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                        padding(Spacing.sm, Spacing.md)
                        property("border-bottom", "1px solid ${colors.border}")
                        opacity(0.75)
                    }
                }) {
                    Div({ style { flex("1") } }) {
                        Div({
                            style {
                                fontSize(Typography.base)
                                fontWeight("600")
                                color(Color(colors.text))
                            }
                        }) { Text(podcast.title) }
                        Div({
                            style { fontSize(Typography.sm); color(Color(colors.textMuted)) }
                        }) { Text(podcast.author ?: "Unknown author") }
                    }
                    Span({
                        style {
                            fontSize(Typography.xs)
                            color(Color(colors.danger))
                            fontWeight("600")
                            padding(2.px, 6.px)
                            border(1.px, LineStyle.Solid, Color(colors.danger))
                            borderRadius(BorderRadius.sm)
                            flexShrink(0)
                        }
                    }) { Text("DELETED") }
                    Button({
                        onClick { onRestore(podcast) }
                        style {
                            padding(Spacing.xs, Spacing.sm)
                            fontSize(Typography.xs)
                            fontWeight("600")
                            color(Color("#ffffff"))
                            backgroundColor(Color(colors.primary))
                            border(0.px)
                            borderRadius(BorderRadius.sm)
                            cursor("pointer")
                            flexShrink(0)
                        }
                    }) { Text("Restore") }
                }
            }
        }
    }

    // Pagination
    Pagination(
        currentPage = currentPage,
        totalCount = totalCount,
        loading = loading,
        onPageChange = onPageChange
    )
}


@Composable
private fun DataTab(podcast: PodcastDto, apiClient: ApiClient, onPodcastUpdate: () -> Unit = {}) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    // Podcast Data Section
    Div({
        style {
            marginBottom(Spacing.xxl)
        }
    }) {
        H4({
            style {
                margin(0.px, 0.px, Spacing.md, 0.px)
                fontSize(Typography.lg)
                fontWeight("600")
            }
        }) { Text("Podcast Information") }

        InfoRow("ID", podcast.id)
        InfoRow("Slug", podcast.slug)
        InfoRow("Language", podcast.language.uppercase())
        InfoRow("Explicit", if (podcast.explicit) "Yes" else "No")
        podcast.author?.let { InfoRow("Author", it) }
        podcast.description?.let { InfoRow("Description", stripHtmlTags(it)) }

        // Podcast Type - Editable
        Div({
            style {
                display(DisplayStyle.Flex)
                marginBottom(Spacing.sm)
                fontSize(Typography.base)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontWeight("600")
                    color(Color(colors.text))
                    minWidth(120.px)
                }
            }) { Text("Type:") }

            var isEditingType by remember { mutableStateOf(false) }
            var selectedType by remember { mutableStateOf(PodcastType.fromString(podcast.type)) }

            if (isEditingType) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(Spacing.sm)
                        alignItems(AlignItems.Center)
                        flex("1")
                    }
                }) {
                    Select({
                        onInput { event ->
                            selectedType = PodcastType.valueOf(event.value!!)
                        }
                        style {
                            padding(Spacing.xs, Spacing.sm)
                            fontSize(Typography.base)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.sm)
                            flex("1")
                        }
                    }) {
                        PodcastType.values().forEach { type ->
                            Option(type.name, { if (selectedType == type) selected() }) {
                                Text(type.displayName)
                            }
                        }
                    }

                    Button({
                        onClick {
                            scope.launch {
                                try {
                                    apiClient.updatePodcast(podcast.id, UpdatePodcastRequest(
                                        type = selectedType.name
                                    ))
                                    onPodcastUpdate()
                                    isEditingType = false
                                } catch (e: Exception) {
                                    console.error("Failed to update type: ${e.message}")
                                }
                            }
                        }
                        style {
                            padding(Spacing.xs, Spacing.md)
                            backgroundColor(Color(colors.success))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.sm)
                            fontSize(Typography.sm)
                            cursor("pointer")
                        }
                    }) { Text("Save") }

                    Button({
                        onClick {
                            selectedType = PodcastType.fromString(podcast.type)
                            isEditingType = false
                        }
                        style {
                            padding(Spacing.xs, Spacing.md)
                            backgroundColor(Color(colors.textMuted))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.sm)
                            fontSize(Typography.sm)
                            cursor("pointer")
                        }
                    }) { Text("Cancel") }
                }
            } else {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(Spacing.sm)
                        alignItems(AlignItems.Center)
                        flex("1")
                    }
                }) {
                    Span({
                        style {
                            color(Color(colors.textMuted))
                            flex("1")
                        }
                    }) { Text(PodcastType.fromString(podcast.type).displayName) }

                    com.castly.admin.ui.components.IconButton(
                        icon = { com.castly.admin.ui.icons.IconEdit(size = 14) },
                        label = "Edit Type",
                        onClick = { isEditingType = true; selectedType = PodcastType.fromString(podcast.type) }
                    )
                }
            }
        }

        // Linked podcast info
        if (podcast.isLinked) {
            Div({
                style {
                    marginTop(Spacing.lg)
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                    borderRadius(BorderRadius.md)
                    themeTransition()
                }
            }) {
                H5({
                    style {
                        margin(0.px, 0.px, Spacing.sm, 0.px)
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                    }
                }) { Text("RSS Linked Podcast") }
                podcast.rssUrl?.let { InfoRow("RSS URL", it) }
                InfoRow("Sync Enabled", if (podcast.syncEnabled) "Yes" else "No")
                InfoRow("Sync Interval", "${podcast.syncIntervalMinutes} minutes")
                podcast.lastSyncedAt?.let { InfoRow("Last Synced", formatTimestamp(it)) }
                podcast.syncError?.let {
                    Div({
                        style {
                            marginTop(Spacing.sm)
                            padding(Spacing.sm)
                            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                            color(Color(colors.danger))
                            borderRadius(BorderRadius.sm)
                            fontSize(Typography.sm)
                            themeTransition()
                        }
                    }) { Text("Sync Error: $it") }
                }
            }
        }

        InfoRow("Created", formatTimestamp(podcast.createdAt))
        InfoRow("Updated", formatTimestamp(podcast.updatedAt))
    }
}

@Composable
private fun EpisodesTab(podcast: PodcastDto, apiClient: ApiClient, onPodcastUpdate: () -> Unit = {}) {
    val colors = AppTheme.colors
    // Pagination state
    var currentPage by remember { mutableStateOf(1) }
    var pageSize by remember { mutableStateOf(20) }
    var totalCount by remember { mutableStateOf(0) }

    // Episode list state
    var episodes by remember { mutableStateOf<List<EpisodeDto>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var editingEpisode by remember { mutableStateOf<EpisodeDto?>(null) }

    // Season tabs state
    var seasons by remember { mutableStateOf<List<Int>>(emptyList()) }
    var selectedSeason by remember { mutableStateOf<Int?>(null) } // null means "All"
    var loadingSeasons by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    fun loadSeasons() {
        scope.launch {
            loadingSeasons = true
            try {
                seasons = apiClient.getSeasons(podcast.id)
            } catch (e: Exception) {
                console.log("Failed to load seasons: ${e.message}")
                seasons = emptyList()
            } finally {
                loadingSeasons = false
            }
        }
    }

    fun loadEpisodes() {
        scope.launch {
            loading = true
            error = null
            try {
                val response = apiClient.listEpisodes(
                    podcast.id,
                    currentPage,
                    pageSize,
                    searchQuery.ifBlank { null },
                    selectedSeason
                )
                episodes = response.items
                totalCount = response.total
            } catch (e: Exception) {
                error = "Failed to load episodes. Please try again."
                episodes = emptyList()
            } finally {
                loading = false
            }
        }
    }

    // Load seasons on mount
    LaunchedEffect(podcast.id) {
        loadSeasons()
        currentPage = 1
        searchQuery = ""
        selectedSeason = null
    }

    // Load episodes when page, search, or season changes
    LaunchedEffect(podcast.id, currentPage, searchQuery, selectedSeason) {
        loadEpisodes()
    }

    // Reset to page 1 when search query or season changes
    LaunchedEffect(searchQuery, selectedSeason) {
        if (currentPage != 1) {
            currentPage = 1
        }
    }

    // Episode Edit/Create Dialog
    editingEpisode?.let { episode ->
        EpisodeEditDialog(
            episode = episode,
            apiClient = apiClient,
            onSave = { request, file ->
                scope.launch {
                    try {
                        val savedEpisode = if (episode.id.isEmpty()) {
                            // Create new episode
                            val createRequest = CreateEpisodeRequest(
                                title = request.title ?: "",
                                description = request.description,
                                audioUrl = request.audioUrl,
                                durationSeconds = request.durationSeconds,
                                fileSizeBytes = request.fileSizeBytes,
                                episodeNumber = request.episodeNumber,
                                seasonNumber = request.seasonNumber,
                                translations = request.translations ?: emptyMap()
                            )
                            apiClient.createEpisode(podcast.id, createRequest)
                        } else {
                            // Update existing episode
                            apiClient.updateEpisode(episode.id, request)
                        }

                        // Upload audio file after saving episode (if file selected)
                        if (file != null) {
                            try {
                                val uploadResponse = apiClient.uploadEpisodeMedia(savedEpisode.id, file)
                                // Update episode with the uploaded file info
                                apiClient.updateEpisode(savedEpisode.id, UpdateEpisodeRequest(
                                    audioUrl = uploadResponse.url,
                                    fileSizeBytes = uploadResponse.size
                                ))
                            } catch (e: Exception) {
                                error = "Episode saved but file upload failed: ${e.message}"
                            }
                        }

                        editingEpisode = null
                        loadEpisodes()
                    } catch (e: Exception) {
                        error = "Failed to ${if (episode.id.isEmpty()) "create" else "update"} episode: ${e.message}"
                    }
                }
            },
            onCancel = { editingEpisode = null }
        )
    }

    // Episodes Section
    Div {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.md)
            }
        }) {
            H4({
                style {
                    margin(0.px)
                    fontSize(Typography.lg)
                    fontWeight("600")
                }
            }) { Text("Episodes (${episodes.size})") }

            // Add New Episode button (hidden for linked podcasts - episodes come from RSS)
            if (!podcast.isLinked) {
                Button({
                    onClick {
                        editingEpisode = EpisodeDto(
                            id = "", // Will be ignored for create
                            podcastId = podcast.id,
                            title = "",
                            slug = "",
                            description = null,
                            audioUrl = null,
                            durationSeconds = null,
                            fileSizeBytes = null,
                            episodeNumber = null,
                            seasonNumber = null,
                            status = EpisodeStatusDto.DRAFT,
                            publishedAt = null,
                            createdAt = "",
                            updatedAt = "",
                            translations = emptyMap()
                        )
                    }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        backgroundColor(Color(colors.success))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight("600")
                    }
                }) { Text("+ New Episode") }
            }
        }

        // Episode search
        Input(InputType.Text) {
            value(searchQuery)
            onInput { searchQuery = it.value }
            attr("placeholder", if (selectedSeason != null) "Search in Season $selectedSeason..." else "Search all episodes...")
            style {
                width(100.percent)
                padding(10.px, 14.px)
                fontSize(Typography.base)
                border(1.px, LineStyle.Solid, Color(colors.border))
                borderRadius(BorderRadius.md)
                marginBottom(Spacing.lg)
                property("outline", "none")
            }
        }

        // Season Tabs - Always show at least "All" tab
        if (!loadingSeasons) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    marginBottom(Spacing.lg)
                    property("border-bottom", "2px solid ${colors.border}")
                    property("overflow-x", "auto")
                }
            }) {
                // "All" tab
                Div({
                    onClick { selectedSeason = null }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight(if (selectedSeason == null) "600" else "400")
                        color(if (selectedSeason == null) Color(colors.primary) else Color(colors.textMuted))
                        property("border-bottom", if (selectedSeason == null) {
                            "2px solid ${colors.primary}"
                        } else {
                            "2px solid transparent"
                        })
                        marginBottom(-2.px)
                        property("white-space", "nowrap")
                    }
                }) { Text("All") }

                // Season tabs (only if seasons exist)
                seasons.forEach { season ->
                    Div({
                        onClick { selectedSeason = season }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            cursor("pointer")
                            fontSize(Typography.base)
                            fontWeight(if (selectedSeason == season) "600" else "400")
                            color(if (selectedSeason == season) Color(colors.primary) else Color(colors.textMuted))
                            property("border-bottom", if (selectedSeason == season) {
                                "2px solid ${colors.primary}"
                            } else {
                                "2px solid transparent"
                            })
                            marginBottom(-2.px)
                            property("white-space", "nowrap")
                        }
                    }) { Text("Season $season") }
                }
            }
        }

        // Loading state UI
        if (loading) {
            Div({
                style {
                    padding(Spacing.lg)
                    textAlign("center")
                }
            }) {
                Text("Loading episodes...")
            }
        }

        // Error state UI
        error?.let { errorMessage ->
            Div({
                style {
                    padding(Spacing.lg)
                    color(Color(colors.danger))
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    border(1.px, LineStyle.Solid, Color(colors.danger))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Text(errorMessage)
                Br()
                Button({
                    style { marginTop(Spacing.sm); marginLeft(0.px); padding(Spacing.sm, Spacing.lg) }
                    onClick { loadEpisodes() } // Retry loading episodes
                }) {
                    Text("Retry")
                }
            }
        }

        if (!loading && error == null) {
            if (episodes.isEmpty()) {
                EmptyState(if (searchQuery.isNotBlank()) "No episodes found" else "No episodes yet")
            } else {
                episodes.forEach { episode ->
                    Div({
                        style {
                            padding(Spacing.md)
                            marginBottom(Spacing.sm)
                            backgroundColor(Color(colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            themeTransition()
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(Spacing.md)
                                alignItems(AlignItems.Start)
                            }
                        }) {
                            // Episode thumbnail (full fallback chain)
                            val imageUrl = episode.imageUrl
                                ?: episode.originalImageUrl
                                ?: podcast.coverArtUrl
                                ?: podcast.originalCoverArtUrl
                            imageUrl?.let { url ->
                                Img(url) {
                                    style {
                                        width(80.px)
                                        height(80.px)
                                        borderRadius(BorderRadius.md)
                                        property("object-fit", "cover")
                                        flexShrink(0)
                                    }
                                }
                            } ?: run {
                                // Placeholder if no image
                                Div({
                                    style {
                                        width(80.px)
                                        height(80.px)
                                        backgroundColor(Color(colors.border))
                                        borderRadius(BorderRadius.md)
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        justifyContent(JustifyContent.Center)
                                        fontSize(Typography.sm)
                                        color(Color(colors.textMuted))
                                        flexShrink(0)
                                    }
                                }) { Text("No Image") }
                            }

                            // Episode details
                            Div({
                                style {
                                    flex("1")
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(Spacing.xs)
                                }
                            }) {
                                // Title
                                Div({
                                    style {
                                        fontWeight("600")
                                        fontSize(Typography.base)
                                    }
                                }) { Text(episode.title) }

                                // Metadata row
                                Div({
                                    style {
                                        fontSize(Typography.sm)
                                        color(Color(colors.textMuted))
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(Spacing.sm)
                                        flexWrap(FlexWrap.Wrap)
                                    }
                                }) {
                                    // Episode & Season number
                                    if (episode.seasonNumber != null && episode.episodeNumber != null) {
                                        Span { Text("S${episode.seasonNumber} E${episode.episodeNumber}") }
                                    } else {
                                        episode.episodeNumber?.let {
                                            Span { Text("Episode #$it") }
                                        }
                                        episode.seasonNumber?.let {
                                            Span { Text("Season $it") }
                                        }
                                    }

                                    // Duration
                                    episode.durationSeconds?.let { seconds ->
                                        val hours = seconds / 3600
                                        val minutes = (seconds % 3600) / 60
                                        val secs = seconds % 60
                                        val durationText = when {
                                            hours > 0 -> "${hours}h ${minutes}m"
                                            minutes > 0 -> "${minutes}m ${secs}s"
                                            else -> "${secs}s"
                                        }
                                        Span { Text("• $durationText") }
                                    }

                                    // Author
                                    val author = episode.translations.values.firstOrNull()?.author ?: podcast.author
                                    author?.let {
                                        Span { Text("• $it") }
                                    }
                                }

                                // Badges row
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(Spacing.sm)
                                        marginTop(Spacing.xs)
                                    }
                                }) {
                                    // RSS badge
                                    if (episode.isLinked) {
                                        Span({
                                            style {
                                                padding(2.px, 6.px)
                                                backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                                                color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                                                borderRadius(BorderRadius.sm)
                                                fontSize(Typography.xs)
                                            }
                                        }) { Text("RSS") }
                                    }

                                    // Status badge
                                    Span({
                                        style {
                                            padding(2.px, 6.px)
                                            backgroundColor(when (episode.status) {
                                                EpisodeStatusDto.PUBLISHED -> Color(if (colors == LightColors) "#d1fae5" else "#052e16")
                                                EpisodeStatusDto.DRAFT -> Color(if (colors == LightColors) "#fef3c7" else "#451a03")
                                                EpisodeStatusDto.ARCHIVED -> Color(colors.border)
                                            })
                                            color(when (episode.status) {
                                                EpisodeStatusDto.PUBLISHED -> Color(if (colors == LightColors) "#065f46" else "#86efac")
                                                EpisodeStatusDto.DRAFT -> Color(if (colors == LightColors) "#92400e" else "#fcd34d")
                                                EpisodeStatusDto.ARCHIVED -> Color(colors.text)
                                            })
                                            borderRadius(BorderRadius.sm)
                                            fontSize(Typography.xs)
                                        }
                                    }) { Text(episode.status.name) }
                                }
                            }

                            // Action buttons
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(Spacing.sm)
                                    flexShrink(0)
                                }
                            }) {
                                // Publish/Unpublish button
                                if (episode.status != EpisodeStatusDto.PUBLISHED) {
                                    IconButton(
                                        icon = { IconCheck(size = 16) },
                                        label = "Publish",
                                        onClick = {
                                            scope.launch {
                                                try {
                                                    apiClient.updateEpisode(episode.id, UpdateEpisodeRequest(
                                                        status = EpisodeStatusDto.PUBLISHED
                                                    ))
                                                    loadEpisodes()
                                                } catch (e: Exception) {
                                                    error = "Failed to publish episode: ${e.message}"
                                                }
                                            }
                                        },
                                        color = colors.success,
                                        hoverColor = colors.success
                                    )
                                }

                                // Edit button
                                IconButton(
                                    icon = { IconEdit(size = 16) },
                                    label = "Edit",
                                    onClick = { editingEpisode = episode },
                                    color = colors.primary,
                                    hoverColor = colors.primaryHover
                                )
                            }
                        }
                    }
                }

                // Pagination controls
                if (!loading && episodes.isNotEmpty() && totalCount > pageSize) {
                    val totalPages = (totalCount + pageSize - 1) / pageSize

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            padding(Spacing.lg)
                            property("border-top", "1px solid ${colors.border}")
                            marginTop(Spacing.lg)
                        }
                    }) {
                        // Page counter
                        Text("Showing ${(currentPage - 1) * pageSize + 1}-${minOf(currentPage * pageSize, totalCount)} of $totalCount episodes")

                        // Navigation buttons
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(Spacing.sm)
                                alignItems(AlignItems.Center)
                            }
                        }) {
                            // Previous button
                            Button({
                                if (currentPage <= 1 || loading) disabled()
                                onClick {
                                    if (currentPage > 1) {
                                        currentPage -= 1
                                    }
                                }
                                style { paginationButtonStyle(currentPage <= 1 || loading, colors) }
                            }) {
                                Text("Previous")
                            }

                            // Current page indicator
                            Span({
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    display(DisplayStyle.InlineBlock)
                                    property("line-height", "1.5")
                                }
                            }) {
                                Text("Page $currentPage of $totalPages")
                            }

                            // Next button
                            Button({
                                if (currentPage >= totalPages || loading) disabled()
                                onClick {
                                    if (currentPage < totalPages) {
                                        currentPage += 1
                                    }
                                }
                                style { paginationButtonStyle(currentPage >= totalPages || loading, colors) }
                            }) {
                                Text("Next")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TagsTab(podcast: PodcastDto, apiClient: ApiClient) {
    val colors = AppTheme.colors
    var currentTags by remember { mutableStateOf<List<TagDto>>(emptyList()) }
    var allTags by remember { mutableStateOf<List<TagWithCountDto>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var tagInput by remember { mutableStateOf("") }
    var showDropdown by remember { mutableStateOf(false) }
    var removeConfirmTag by remember { mutableStateOf<TagDto?>(null) }
    val scope = rememberCoroutineScope()

    fun loadTags() {
        scope.launch {
            loading = true
            error = null
            try {
                currentTags = apiClient.getPodcastTags(podcast.id)
                allTags = apiClient.listTags()
            } catch (e: Exception) {
                error = "Failed to load tags: ${e.message}"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(podcast.id) {
        loadTags()
    }

    fun addTag(request: AddTagRequest) {
        scope.launch {
            try {
                val newTag = apiClient.addPodcastTag(podcast.id, request)
                currentTags = currentTags + newTag
                tagInput = ""
                showDropdown = false
                // Reload all tags to get updated counts
                allTags = apiClient.listTags()
            } catch (e: Exception) {
                error = "Failed to add tag: ${e.message}"
            }
        }
    }

    fun removeTag(tagId: String) {
        scope.launch {
            try {
                apiClient.removePodcastTag(podcast.id, tagId)
                currentTags = currentTags.filter { it.id != tagId }
                removeConfirmTag = null
                // Reload all tags to get updated counts
                allTags = apiClient.listTags()
            } catch (e: Exception) {
                error = "Failed to remove tag: ${e.message}"
            }
        }
    }

    val filteredTags = if (tagInput.isBlank()) {
        allTags
    } else {
        allTags.filter { it.name.contains(tagInput, ignoreCase = true) }
    }

    val alreadyAdded = currentTags.map { it.id }.toSet()

    // Validate tag name format
    val tagNamePattern = Regex("^[a-zA-Z0-9\\s\\-]*$")
    val isValidTagName = tagInput.isBlank() || (tagInput.length <= 50 && tagNamePattern.matches(tagInput))
    val validationError = when {
        tagInput.isBlank() -> null
        tagInput.length > 50 -> "Tag name must be 50 characters or less"
        !tagNamePattern.matches(tagInput) -> "Tag name can only contain letters, numbers, spaces, and hyphens"
        else -> null
    }

    Div({
        style {
            padding(Spacing.xl)
        }
    }) {
        H4({
            style {
                margin(0.px, 0.px, Spacing.lg, 0.px)
                fontSize(Typography.lg)
                fontWeight("600")
            }
        }) { Text("Tags") }

        // Error message
        error?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                    borderRadius(BorderRadius.md)
                    marginBottom(Spacing.lg)
                    fontSize(13.px)
                    themeTransition()
                }
            }) { Text(msg) }
        }

        // Current tags display
        if (currentTags.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(Spacing.sm)
                    marginBottom(Spacing.xl)
                }
            }) {
                currentTags.forEach { tag ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                            padding(6.px, Spacing.md)
                            backgroundColor(Color(if (colors == LightColors) "#e0e7ff" else "#1e1b4b"))
                            color(Color(if (colors == LightColors) "#3730a3" else "#a5b4fc"))
                            borderRadius(16.px)
                            fontSize(13.px)
                            fontWeight("500")
                        }
                    }) {
                        Text(tag.name)
                        Button({
                            onClick { removeConfirmTag = tag }
                            style {
                                background("none")
                                border(0.px)
                                color(Color(if (colors == LightColors) "#3730a3" else "#a5b4fc"))
                                cursor("pointer")
                                padding(0.px)
                                fontSize(Typography.base)
                                fontWeight("600")
                                lineHeight("1")
                            }
                        }) { Text("×") }
                    }
                }
            }
        }

        // Tag input with autocomplete
        Div({
            style {
                position(Position.Relative)
                marginBottom(Spacing.lg)
            }
        }) {
            Input(InputType.Text) {
                value(tagInput)
                onInput { tagInput = it.value }
                onFocus { showDropdown = true }
                onBlur {
                    // Delay to allow click to register on dropdown item
                    scope.launch {
                        kotlinx.coroutines.delay(200)
                        showDropdown = false
                    }
                }
                placeholder("Add tags...")
                style {
                    width(100.percent)
                    padding(10.px, Spacing.md)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    property("box-sizing", "border-box")
                }
            }

            // Validation error message
            if (validationError != null) {
                Div({
                    style {
                        marginTop(Spacing.xs)
                        fontSize(Typography.sm)
                        color(Color(colors.danger))
                    }
                }) { Text(validationError) }
            }

            // Autocomplete dropdown
            if (showDropdown && (filteredTags.isNotEmpty() || tagInput.isNotBlank())) {
                Div({
                    style {
                        position(Position.Absolute)
                        top(100.percent)
                        left(0.px)
                        right(0.px)
                        marginTop(Spacing.xs)
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        maxHeight(200.px)
                        property("overflow-y", "auto")
                        themeTransition()
                    }
                }) {
                    // Show matching existing tags
                    filteredTags.forEach { tag ->
                        if (tag.id !in alreadyAdded) {
                            Div({
                                onClick {
                                    addTag(AddTagRequest(tagId = tag.id))
                                }
                                style {
                                    padding(10.px, Spacing.md)
                                    cursor("pointer")
                                    property("&:hover", "background-color: ${colors.surfaceHover}")
                                    fontSize(Typography.base)
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center)
                                }
                            }) {
                                Span { Text(tag.name) }
                                Span({
                                    style {
                                        fontSize(Typography.sm)
                                        color(Color(colors.textMuted))
                                    }
                                }) { Text("(${tag.podcastCount})") }
                            }
                        }
                    }

                    // Show "Create" option if input doesn't match any tag and is valid
                    if (tagInput.isNotBlank() && filteredTags.all { it.name != tagInput } && isValidTagName) {
                        Div({
                            onClick {
                                addTag(AddTagRequest(name = tagInput))
                            }
                            style {
                                padding(10.px, Spacing.md)
                                cursor("pointer")
                                property("&:hover", "background-color: ${colors.surfaceHover}")
                                fontSize(Typography.base)
                                color(Color(colors.success))
                                fontWeight("500")
                            }
                        }) {
                            Text("Create \"$tagInput\"")
                        }
                    }
                }
            }
        }

        // Loading indicator
        if (loading) {
            Div({
                style {
                    fontSize(13.px)
                    color(Color(colors.textMuted))
                    fontStyle("italic")
                }
            }) { Text("Loading...") }
        }
    }

    // Remove confirmation dialog
    removeConfirmTag?.let { tag ->
        ConfirmationDialog(
            title = "Remove Tag?",
            message = "Are you sure you want to remove the tag \"${tag.name}\" from this podcast?",
            onConfirm = { removeTag(tag.id) },
            onCancel = { removeConfirmTag = null }
        )
    }
}

@Composable
private fun TranslationsTab(podcast: PodcastDto, apiClient: ApiClient, onPodcastUpdate: (String) -> Unit) {
    val colors = AppTheme.colors
    var editingTranslation by remember { mutableStateOf<Pair<String, PodcastTranslationData>?>(null) }
    var isAddingNew by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    var error by remember { mutableStateOf<String?>(null) }

    // Available languages (excluding already added ones)
    val allLanguages = listOf("en" to "English", "ar" to "Arabic", "fr" to "French", "es" to "Spanish", "de" to "German")
    val availableLanguages = allLanguages.filter { (code, _) -> !podcast.translations.containsKey(code) }

    // Translation Edit/Add Dialog
    editingTranslation?.let { (lang, translation) ->
        PodcastTranslationEditDialog(
            language = lang,
            translation = translation,
            podcastId = podcast.id,
            isNew = isAddingNew,
            onSave = { updatedTranslation ->
                scope.launch {
                    try {
                        // We need to send all translations when updating
                        val allTranslations = podcast.translations.toMutableMap()
                        allTranslations[lang] = updatedTranslation

                        apiClient.updatePodcast(podcast.id, UpdatePodcastRequest(
                            translations = allTranslations
                        ))
                        editingTranslation = null
                        isAddingNew = false
                        onPodcastUpdate(podcast.id)
                    } catch (e: Exception) {
                        error = "Failed to ${if (isAddingNew) "add" else "update"} translation: ${e.message}"
                    }
                }
            },
            onCancel = {
                editingTranslation = null
                isAddingNew = false
            }
        )
    }

    Div({
        style {
            padding(Spacing.xl)
        }
    }) {
        // Header with Add button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.lg)
            }
        }) {
            H4({
                style {
                    margin(0.px)
                    fontSize(Typography.lg)
                    fontWeight("600")
                }
            }) { Text("Translations") }

            // Add Translation dropdown button
            if (availableLanguages.isNotEmpty()) {
                var showDropdown by remember { mutableStateOf(false) }

                Div({
                    style {
                        position(Position.Relative)
                    }
                }) {
                    Button({
                        onClick { showDropdown = !showDropdown }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            backgroundColor(Color(colors.success))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            fontSize(Typography.base)
                            fontWeight("500")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                        }
                    }) {
                        Text("+ Add Translation")
                    }

                    // Dropdown menu
                    if (showDropdown) {
                        Div({
                            style {
                                position(Position.Absolute)
                                top(100.percent)
                                right(0.px)
                                marginTop(Spacing.xs)
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                property("box-shadow", "0 4px 6px -1px rgba(0, 0, 0, 0.1)")
                                property("z-index", "100")
                                minWidth(150.px)
                                themeTransition()
                            }
                        }) {
                            availableLanguages.forEach { (code, name) ->
                                Div({
                                    onClick {
                                        isAddingNew = true
                                        editingTranslation = Pair(code, PodcastTranslationData(title = "", description = null, author = null))
                                        showDropdown = false
                                    }
                                    style {
                                        padding(10.px, Spacing.lg)
                                        cursor("pointer")
                                        fontSize(Typography.base)
                                        color(Color(colors.text))
                                    }
                                    onMouseOver {
                                        (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = colors.surfaceHover
                                    }
                                    onMouseOut {
                                        (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "transparent"
                                    }
                                }) {
                                    Text("$name ($code)")
                                }
                            }
                        }
                    }
                }
            }
        }

        error?.let { msg ->
            ErrorMessage(msg)
        }

        if (podcast.translations.isEmpty()) {
            EmptyState("No translations available. Click 'Add Translation' to add one.")
        } else {
            podcast.translations.forEach { (lang, translation) ->
                Div({
                    style {
                        padding(Spacing.lg)
                        marginBottom(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.md)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        themeTransition()
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            marginBottom(Spacing.md)
                        }
                    }) {
                        Span({
                            style {
                                padding(Spacing.xs, Spacing.md)
                                backgroundColor(Color(colors.primary))
                                color(Color("#ffffff"))
                                borderRadius(BorderRadius.sm)
                                fontWeight("600")
                                fontSize(Typography.base)
                            }
                        }) { Text(lang.uppercase()) }

                        // Edit button
                        IconButton(
                            icon = { IconEdit(size = 16) },
                            label = "Edit Translation",
                            onClick = { editingTranslation = Pair(lang, translation) },
                            color = colors.primary,
                            hoverColor = colors.primaryHover
                        )
                    }

                    InfoRow("Title", translation.title)
                    translation.description?.let { InfoRow("Description", stripHtmlTags(it)) }
                    translation.author?.let { InfoRow("Author", it) }
                }
            }
        }
    }
}

